import { ReactElement } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter';
import { cn } from '@/lib/utils';

interface AnimatedMetricCardProps {
  title: string;
  value: number;
  suffix?: string;
  description: string;
  icon: ReactElement;
  isLoading: boolean;
  gradient: string;
  iconColor: string;
  delay?: number;
}

export function AnimatedMetricCard({ 
  title, 
  value, 
  suffix = '', 
  description, 
  icon, 
  isLoading, 
  gradient,
  iconColor,
  delay = 0
}: AnimatedMetricCardProps) {
  const { value: animatedValue, isAnimating } = useAnimatedCounter({ 
    end: value, 
    duration: 1500,
    delay: delay * 200 // Stagger animations
  });

  return (
    <Card className={cn(
      "relative overflow-hidden border border-[#e6e8eb]/70 hover:border-[#0248c1]/30 transition-all duration-500 hover:shadow-lg hover:scale-[1.02] group",
      gradient
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-4 pt-4">
        <CardTitle className="text-base md:text-lg font-medium">{title}</CardTitle>
        <div className={cn(
          "h-6 w-6 transition-colors duration-300 group-hover:scale-110", 
          iconColor
        )}>
          {icon}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className={cn(
          "text-3xl md:text-4xl font-bold transition-all duration-300",
          isAnimating && "animate-bounce-subtle"
        )}>
          {isLoading ? (
            <div className="w-16 h-10 bg-muted animate-pulse rounded"></div>
          ) : (
            <span className="bg-gradient-to-r from-[#111835] to-[#0248c1] bg-clip-text text-transparent">
              {animatedValue}{suffix}
            </span>
          )}
        </div>
        <p className="text-sm md:text-base text-muted-foreground mt-1">{description}</p>
      </CardContent>
      
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#f8d613]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </Card>
  );
}